import { Link, createFileRoute } from "@tanstack/react-router";

import { CihClosingCta } from "~/components/cih/closing-cta";
import { CihLogoTile } from "~/components/cih/logo-tile";
import { Breadcrumb } from "~/components/ui/breadcrumb";
import { Button } from "~/components/ui/button";
import { getAllPublishedCtps } from "~/lib/functions/ctps";
import { BRAND_URL } from "~/lib/shared/brand";
import {
  createBrandMeta,
  createBreadcrumbStructuredData,
} from "~/lib/shared/meta";

type CtpRow = Awaited<ReturnType<typeof getAllPublishedCtps>>[number];

const CANONICAL_URL = `${BRAND_URL}/training-programs`;

export const Route = createFileRoute("/_public/training-programs/")({
  staticData: { title: "Training Programs" },
  loader: async () => ({ ctps: await getAllPublishedCtps() }),
  head: () => ({
    ...createBrandMeta({
      title: "Coaching Training Programs | Dream Coach Match",
      // Describes what the page shows — programs grouped by the school that
      // delivers them. It cannot call them accredited: `ctp.cco` proves only
      // delivery, and a program's accreditors are a separate relationship that
      // many programs will not have recorded.
      description:
        "Coach training programs worldwide, grouped by provider — from broad certification curricula to specialty methodology trainings.",
      ogUrl: CANONICAL_URL,
      canonical: CANONICAL_URL,
    }),
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify(
          createBreadcrumbStructuredData([
            { name: "Home", url: BRAND_URL },
            { name: "Training programs", url: CANONICAL_URL },
          ]),
        ),
      },
    ],
  }),
  component: TrainingProgramsIndex,
});

function TrainingProgramsIndex() {
  const { ctps } = Route.useLoaderData();

  // Group by the delivering school's name — `ctp.cco` is the provider, not an
  // accreditor.
  const grouped = ctps.reduce<Record<string, CtpRow[]>>((acc, ctp) => {
    const key = ctp.cco?.name ?? "Unaffiliated";
    (acc[key] ??= []).push(ctp);
    return acc;
  }, {});
  const groupNames = Object.keys(grouped).sort();

  return (
    <>
      <section className="border-b border-gray-200 bg-white">
        <div className="mx-auto max-w-7xl px-4 pt-6 pb-14 md:pt-8 md:pb-20 xl:px-0">
          <Breadcrumb
            items={[
              { label: "Home", href: "/" },
              { label: "Training programs" },
            ]}
            isBold={false}
          />
          <div className="mt-6 text-xs font-medium tracking-[0.22em] text-orange-700 uppercase">
            ● Coaching Intelligence
          </div>
          <h1 className="mt-4 font-heading text-4xl font-medium tracking-tight text-gray-900 md:text-5xl lg:text-6xl xl:text-7xl">
            Training programs
          </h1>
          <p className="mt-5 max-w-xl text-lg leading-normal text-gray-700">
            Coach training programs worldwide, grouped by provider.
          </p>
        </div>
      </section>

      <div className="mx-auto max-w-7xl px-4 py-14 md:py-20 xl:px-0">
        {groupNames.map((name) => (
          <section key={name} className="mb-14">
            <div className="mb-5 text-xs font-medium tracking-[0.22em] text-gray-400 uppercase">
              Provider &nbsp;·&nbsp; {name}
            </div>
            <div className="border-t border-gray-200">
              {(grouped[name] ?? []).map((ctp) => (
                <Link
                  key={ctp.id}
                  to="/training-programs/$slug"
                  params={{ slug: ctp.slug ?? "" }}
                  className="group grid grid-cols-[48px_1fr_auto] items-center gap-5 border-b border-gray-200 px-0 py-5 transition-colors hover:bg-gray-50 md:grid-cols-[56px_1fr_auto_auto] md:gap-6 md:py-6"
                >
                  <CihLogoTile
                    name={ctp.name}
                    logoUrl={ctp.logoUrl ?? ctp.displayLogoUrl}
                    className="h-12 w-12 md:h-14 md:w-14"
                  />
                  <div>
                    <h3 className="text-base font-medium text-gray-900">
                      {ctp.name}
                    </h3>
                    <p className="mt-0.5 text-xs text-gray-500">
                      {[
                        ctp.accreditationLevel,
                        ctp.duration,
                        ctp.deliveryFormat,
                      ]
                        .filter(Boolean)
                        .join(" · ")}
                    </p>
                    {ctp.frameworks > 0 ? (
                      <p className="mt-1 text-[11px] font-medium tracking-wider text-gray-500 uppercase">
                        {`${ctp.frameworks} ${ctp.frameworks === 1 ? "framework" : "frameworks"}`}
                      </p>
                    ) : null}
                  </div>
                  {ctp.tuitionRange ? (
                    <div className="hidden text-right text-xs text-gray-500 md:block">
                      <div className="text-sm font-medium text-gray-900">
                        {ctp.tuitionRange}
                      </div>
                      Tuition
                    </div>
                  ) : null}
                  <span className="text-gray-400 group-hover:text-gray-900">
                    →
                  </span>
                </Link>
              ))}
            </div>
          </section>
        ))}
      </div>

      <CihClosingCta
        headline="Find a coach trained at one of these programs."
        cta={
          <Link to="/discovery">
            <Button variant="primary" size="lg">
              Start →
            </Button>
          </Link>
        }
      />
    </>
  );
}
